import { useMemo } from 'react';

/**
 * Centralized hook for position P&L calculations (broker AveragePrice / UnrealizedProfitLoss).
 */
const usePositionCalculations = () => {
  /**
   * Get unrealized P&L from position
   * @param {Object} position - Position object from TradeStation API
   * @returns {number} - Unrealized P&L in dollars
   */
  const calculateUnrealizedPL = useMemo(() => {
    return (position) => {
      if (!position) {
        return 0;
      }
      
      const assetType = position.AssetType;
      
      // For futures, use broker's value directly since it includes multipliers
      if (assetType === 'FUTURE') {
        return parseFloat(position.UnrealizedProfitLoss) || 0;
      }
      
      // For equity/stock, calculate in real-time from Last price for real-time updates
      const currentPrice = parseFloat(position.Last) || parseFloat(position.MarkToMarketPrice) || 0;
      const avgPrice = parseFloat(position.AveragePrice) || 0;
      const quantity = parseFloat(position.Quantity) || 0;
      const ls = position.LongShort?.toLowerCase()?.trim();
      const isLong = ls === 'long' ? true : ls === 'short' ? false : quantity >= 0;
      
      if (avgPrice === 0 || quantity === 0) {
        // Fallback to broker's value if we don't have the data
        return parseFloat(position.UnrealizedProfitLoss) || 0;
      }
      
      // Use absolute value of quantity since direction is determined by isLong
      // TradeStation API returns negative quantity for short positions
      const absQuantity = Math.abs(quantity);
      
      if (isLong) {
        return (currentPrice - avgPrice) * absQuantity;
      } else {
        return (avgPrice - currentPrice) * absQuantity;
      }
    };
  }, []);

  /**
   * Get unrealized P&L percentage from position
   * @param {Object} position - Position object from TradeStation API
   * @returns {number} - Unrealized P&L as a percentage
   */
  const calculateUnrealizedPLPercent = useMemo(() => {
    return (position) => {
      if (!position) {
        return 0;
      }

      // Consistent TS + CT formula:
      // overall % = unrealized_pl / (avg_price * abs(qty)) * 100
      const unrealizedPL = calculateUnrealizedPL(position);
      const avgPrice = parseFloat(position.AveragePrice) || 0;
      const qty = Math.abs(parseFloat(position.Quantity) || 0);
      const costBasis = avgPrice * qty;

      if (!Number.isFinite(unrealizedPL) || !Number.isFinite(costBasis) || costBasis <= 0) {
        return 0;
      }
      return (unrealizedPL / costBasis) * 100;
    };
  }, [calculateUnrealizedPL]);

  /**
   * Calculate market value for a position
   * @param {Object} position - Position object from TradeStation API
   * @returns {number} - Current market value in dollars
   */
  const calculateMarketValue = useMemo(() => {
    return (position) => {
      if (!position) {
        return 0;
      }
      
      const currentPrice = parseFloat(position.Last) || parseFloat(position.MarkToMarketPrice) || 0;
      const quantity = parseFloat(position.Quantity) || 0;
      // Use absolute value since TradeStation returns negative quantity for short positions
      const fromPrice = currentPrice * Math.abs(quantity);
      if (fromPrice > 0) return fromPrice;
      // Tastytrade often sends market value without a last price.
      const explicit = parseFloat(position.MarketValue);
      return Number.isFinite(explicit) ? Math.abs(explicit) : 0;
    };
  }, []);

  /**
   * Today's P&L as % of current position value (matches PositionsTable / broker convention).
   * Not the same as unrealized % (UnrealizedProfitLossPercent).
   */
  const calculateTodaysPLPercent = useMemo(() => {
    return (position) => {
      if (!position) return 0;
      const todaysPL = parseFloat(position.TodaysProfitLoss);
      if (todaysPL === undefined || todaysPL === null || Number.isNaN(todaysPL)) return 0;
      const qty = parseFloat(position.Quantity);
      if (!qty) return 0;
      const price = parseFloat(position.MarkToMarketPrice) || parseFloat(position.Last) || 0;
      if (!price) return 0;
      const positionValue = price * Math.abs(qty);
      return (todaysPL / positionValue) * 100;
    };
  }, []);

  return {
    calculateUnrealizedPL,
    calculateUnrealizedPLPercent,
    calculateMarketValue,
    calculateTodaysPLPercent
  };
};

export default usePositionCalculations;
